
import React, { useState, useMemo } from 'react';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Project } from '@/types/Project';
import { Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';

interface MonthlyProjectViewProps {
  timeEntries: any[];
  projects: Project[];
  hasTimeEntries: boolean;
}

export function MonthlyProjectView({ timeEntries, projects, hasTimeEntries }: MonthlyProjectViewProps) {
  const [monthsToShow, setMonthsToShow] = useState<string>("6");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: subMonths(new Date(), parseInt(monthsToShow) - 1),
    to: new Date()
  });
  
  // Update date range when months to show changes
  React.useEffect(() => {
    setDateRange({
      from: subMonths(new Date(), parseInt(monthsToShow) - 1),
      to: new Date()
    });
  }, [monthsToShow]);
  
  const monthlyProjectData = useMemo(() => {
    if (!hasTimeEntries || !projects.length) return [];
    
    // Generate array of months
    const months = eachMonthOfInterval({
      start: startOfMonth(dateRange.from),
      end: endOfMonth(dateRange.to)
    });

    // Initialize data structure
    const monthlyData = months.map(month => {
      const monthKey = format(month, 'yyyy-MM');
      const monthName = format(month, 'MMMM yyyy');
      
      // Initialize with zero hours and revenue for each project
      const projectHours: Record<string, number> = {};
      const projectRevenue: Record<string, number> = {};
      projects.forEach(project => {
        projectHours[project.id] = 0;
        projectRevenue[project.id] = 0;
      });
      
      return {
        month: monthKey,
        monthName,
        projectHours,
        projectRevenue,
        totalHours: 0,
        totalRevenue: 0
      };
    });
    
    // Filter entries by selected project
    const filteredEntries = selectedProjectId === 'all' 
      ? timeEntries 
      : timeEntries.filter(entry => entry.projectId === selectedProjectId);
    
    // Populate with actual data
    filteredEntries.forEach(entry => {
      const entryDate = new Date(entry.date);
      
      // Skip if not in date range
      if (entryDate < dateRange.from || entryDate > dateRange.to) return;
      
      const monthKey = format(entryDate, 'yyyy-MM');
      const monthData = monthlyData.find(m => m.month === monthKey);
      
      if (monthData && entry.projectId) {
        const minutes = entry.minutes || 0;
        const hours = minutes / 60;
        
        // Find project to get hourly rate
        const project = projects.find(p => p.id === entry.projectId);
        const hourlyRate = project?.hourlyRate || 0;
        const revenue = hours * hourlyRate;
        
        // Add to project hours and revenue
        if (monthData.projectHours[entry.projectId] !== undefined) {
          monthData.projectHours[entry.projectId] += hours;
          monthData.projectRevenue[entry.projectId] += revenue;
          monthData.totalHours += hours;
          monthData.totalRevenue += revenue;
        }
      }
    });
    
    // Sort by most recent month first
    return monthlyData.reverse();
  }, [timeEntries, projects, hasTimeEntries, monthsToShow, selectedProjectId, dateRange]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!monthlyProjectData.length) return { totalHours: 0, avgHoursPerMonth: 0, totalRevenue: 0, avgRevenuePerMonth: 0 };
    
    const totalHours = monthlyProjectData.reduce((sum, month) => sum + month.totalHours, 0);
    const totalRevenue = monthlyProjectData.reduce((sum, month) => sum + month.totalRevenue, 0);
    const avgHoursPerMonth = totalHours / monthlyProjectData.length;
    const avgRevenuePerMonth = totalRevenue / monthlyProjectData.length;
    
    return {
      totalHours: parseFloat(totalHours.toFixed(2)),
      avgHoursPerMonth: parseFloat(avgHoursPerMonth.toFixed(2)),
      totalRevenue: parseFloat(totalRevenue.toFixed(2)),
      avgRevenuePerMonth: parseFloat(avgRevenuePerMonth.toFixed(2))
    };
  }, [monthlyProjectData]);

  if (!hasTimeEntries) {
    return null;
  }

  // Determine which projects to show in the table based on filtering
  const projectsToShow = selectedProjectId === 'all' 
    ? projects 
    : projects.filter(p => p.id === selectedProjectId);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="py-5">
            <CardTitle className="text-lg">Total Hours</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-3xl font-bold">{summaryStats.totalHours.toFixed(2)}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {selectedProjectId === 'all' 
                ? 'Across all projects' 
                : `For ${projects.find(p => p.id === selectedProjectId)?.name}`}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-5">
            <CardTitle className="text-lg">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-3xl font-bold">
              ${summaryStats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Based on project rates
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-5">
            <CardTitle className="text-lg">Monthly Average</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold">{summaryStats.avgHoursPerMonth.toFixed(2)}h</div>
            <div className="text-lg font-semibold text-muted-foreground mt-1">
              ${summaryStats.avgRevenuePerMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Per month average
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-5">
            <CardTitle className="text-lg">Time Period</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-lg font-medium">
              {format(dateRange.from, 'MMM yyyy')} - {format(dateRange.to, 'MMM yyyy')}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {monthlyProjectData.length} months
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Monthly Project Breakdown</CardTitle>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Project:</span>
              </div>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Period:</span>
              <Select value={monthsToShow} onValueChange={setMonthsToShow}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  {projectsToShow.map(project => (
                    <TableHead key={project.id} className="text-right">
                      <div>{project.name}</div>
                      <div className="text-xs text-muted-foreground font-normal">
                        Hours / Revenue
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">
                    <div>Total</div>
                    <div className="text-xs text-muted-foreground font-normal">
                      Hours / Revenue
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyProjectData.map(monthData => (
                  <TableRow key={monthData.month}>
                    <TableCell className="font-medium">{monthData.monthName}</TableCell>
                    {projectsToShow.map(project => (
                      <TableCell key={project.id} className="text-right">
                        {monthData.projectHours[project.id] > 0 ? (
                          <div>
                            <div>{monthData.projectHours[project.id].toFixed(2)}h</div>
                            <div className="text-xs text-muted-foreground">
                              ${monthData.projectRevenue[project.id].toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div>0.00h</div>
                            <div className="text-xs text-muted-foreground">$0.00</div>
                          </div>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-semibold">
                      <div>{monthData.totalHours.toFixed(2)}h</div>
                      <div className="text-sm text-muted-foreground font-normal">
                        ${monthData.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
